import {
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_FILE_SIZE,
  MAX_CHAT_IMAGES,
  MAX_CHAT_IMAGE_SIZE,
} from 'constants/files'
import { DraftAttachment } from 'types/Chat'
import { isChatImage } from 'utils/getChatImageMimeType'

interface ChatAttachmentSelection {
  files: File[]
  errorKeys: string[]
}

interface ChatAttachmentLimit {
  maxCount: number
  maxSize: number
  countErrorKey: string
  sizeErrorKey: string
}

const FILE_LIMIT: ChatAttachmentLimit = {
  maxCount: MAX_CHAT_ATTACHMENTS,
  maxSize: MAX_CHAT_FILE_SIZE,
  countErrorKey: 'aiChat.errors.attachmentCount',
  sizeErrorKey: 'aiChat.errors.attachmentSize',
}

const IMAGE_LIMIT: ChatAttachmentLimit = {
  maxCount: MAX_CHAT_IMAGES,
  maxSize: MAX_CHAT_IMAGE_SIZE,
  countErrorKey: 'aiChat.errors.imageCount',
  sizeErrorKey: 'aiChat.errors.imageSize',
}

const countChatImages = (attachments: Partial<DraftAttachment>[] = []) => (
  attachments.filter(({ file }) => file && isChatImage(file)).length
)

const isChatAttachmentLimitReached = (attachments: DraftAttachment[]) => {
  const imageCount = countChatImages(attachments)

  return imageCount >= MAX_CHAT_IMAGES && attachments.length - imageCount >= MAX_CHAT_ATTACHMENTS
}

const selectChatAttachmentFiles = (
  attachments: DraftAttachment[],
  selectedFiles: File[],
): ChatAttachmentSelection => {
  const imageCount = countChatImages(attachments)
  const acceptedFiles = new Set<File>()
  const errorKeys: string[] = []
  const select = (files: File[], limit: ChatAttachmentLimit, attachedCount: number) => {
    const eligibleFiles = files.filter((file) => file.size <= limit.maxSize)
    const availableSlots = Math.max(limit.maxCount - attachedCount, 0)

    if (eligibleFiles.length !== files.length) errorKeys.push(limit.sizeErrorKey)
    if (eligibleFiles.length > availableSlots) errorKeys.push(limit.countErrorKey)
    eligibleFiles.slice(0, availableSlots).forEach((file) => acceptedFiles.add(file))
  }

  select(
    selectedFiles.filter((file) => !isChatImage(file)),
    FILE_LIMIT,
    attachments.length - imageCount,
  )
  select(selectedFiles.filter(isChatImage), IMAGE_LIMIT, imageCount)

  return { files: selectedFiles.filter((file) => acceptedFiles.has(file)), errorKeys }
}

export { countChatImages, isChatAttachmentLimitReached, selectChatAttachmentFiles }
